// =============================================================================
//  SCREEN: FinanceScreen (mit optimiertem Quittungsdruck)
// =============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';

const FinanceScreen = () => {
  const { id: tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, entriesRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/entries`),
        ]);
        setTournament(tournamentRes.data);
        setEntries(entriesRes.data);
      } catch (err) {
        toast.error('Fehler beim Laden der Daten.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tournamentId]);
  
  const handleListChange = (listName, index, field, value) => {
    const newList = [...(tournament.finances[listName] || [])];
    newList[index][field] = value;
    setTournament(prev => ({ ...prev, finances: { ...prev.finances, [listName]: newList } }));
  };

  const handleAddListItem = (listName, newItem) => {
    const newList = [...(tournament.finances[listName] || []), newItem];
    setTournament(prev => ({ ...prev, finances: { ...prev.finances, [listName]: newList } }));
  };

  const handleRemoveListItem = (listName, index) => {
    const newList = (tournament.finances[listName] || []).filter((_, i) => i !== index);
    setTournament(prev => ({ ...prev, finances: { ...prev.finances, [listName]: newList } }));
  };

  const handlePaymentChange = (referenceId, name, amount) => {
    const payments = [...(tournament.finances.payments || [])];
    const paymentIndex = payments.findIndex(p => p.referenceId === referenceId);
    const amountPaid = parseFloat(amount) || 0;

    if (paymentIndex > -1) {
      if (amountPaid > 0) {
        payments[paymentIndex].amount = amountPaid;
      } else {
        payments.splice(paymentIndex, 1);
      }
    } else if(amountPaid > 0) {
      payments.push({ referenceId, name, amount: amountPaid });
    }
    setTournament(prev => ({ ...prev, finances: { ...prev.finances, payments } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/api/tournaments/${tournamentId}`, { finances: tournament.finances });
      toast.success('Finanzdaten aktualisiert!');
    } catch (err) {
      toast.error('Fehler beim Speichern der Finanzdaten.');
    }
  };

  // HILFSFUNKTION: Zeichnet eine einzelne A5-Quittung an einer bestimmten Y-Position
  const drawReceipt = (doc, participant, amount, startY) => {
    const isBlanko = !participant;
    const participantName = isBlanko ? '____________________________' : participant.name;
    const amountText = !amount ? '__________ €' : `${parseFloat(amount).toFixed(2)} €`;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Zahlungsbestätigung Nenngeld', 105, startY, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    // KORREKTUR: Expliziter Zeilenumbruch für langen Text
    const textLines = doc.splitTextToSize(`Hiermit bestätigen wir den Erhalt des Nenngeldes für die Teilnahme am Turnier`, 170);
    doc.text(textLines, 20, startY + 20);
    
    doc.setFont('helvetica', 'bold');
    doc.text(`"${tournament.name}"`, 20, startY + 32);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`von:`, 20, startY + 42);
    doc.setFont('helvetica', 'bold');
    doc.text(participantName, 30, startY + 42);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`in Höhe von:`, 20, startY + 52);
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(amountText, 20, startY + 62);
    doc.rect(18, startY + 56, 40, 10);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Vielen Dank für die Teilnahme und "Gut Holz!"', 20, startY + 75);
    
    const today = new Date().toLocaleDateString('de-DE');
   // doc.text(`Datum: ${today}`, 20, startY + 95);

   doc.text(`Datum: _______`, 20, startY + 95);
    
    doc.line(80, startY + 110, 190, startY + 110);
    doc.text('Unterschrift (Veranstalter)', 82, startY + 115);
  };
  
  // Funktion für eine einzelne Quittung
  const generateSingleReceiptPDF = (participant, amount) => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a5' });
    drawReceipt(doc, participant, amount, 20); // Zeichnet eine Quittung im Querformat
    const fileName = !participant ? 'Quittung_Nenngeld_Blanko.pdf' : `Quittung_Nenngeld_${participant.name.replace(/\s/g, '_')}.pdf`;
    doc.save(fileName);
  };
  
  // NEUE FUNKTION: Druckt alle Quittungen (ohne Betrag) auf A4
  const generateAllReceiptsPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    for (let i = 0; i < participants.length; i += 2) {
      if (i > 0) {
        doc.addPage();
      }
      // Erste Quittung (oben auf der Seite)
      drawReceipt(doc, participants[i], null, 20);

      doc.setLineDashPattern([2, 2], 0);
      doc.line(10, 148.5, 200, 148.5); // Trennlinie in der Mitte
      doc.setLineDashPattern([], 0);

      // Zweite Quittung (unten auf der Seite), falls vorhanden
      if (participants[i + 1]) {
        drawReceipt(doc, participants[i + 1], null, 165);
      }
    }
    doc.save(`Alle_Quittungen_${tournament.name.replace(/\s/g, '_')}.pdf`);
  };

  const { participants, totalRevenue } = useMemo(() => {
    if (!tournament || !entries) return { participants: [], totalRevenue: 0 };
    
    const singleStarters = entries.filter(e => e && e.player && !e.team);
    const teams = entries.reduce((acc, entry) => {
      if (entry && entry.team) acc[entry.team._id] = entry.team;
      return acc;
    }, {});

    const participants = [
      ...Object.values(teams).map(t => ({ id: t._id, name: t.name, type: 'team' })),
      ...singleStarters.map(e => ({ id: e._id, name: `${e.player.lastName}, ${e.player.firstName}`, type: 'single' }))
    ].sort((a,b) => a.name.localeCompare(b.name));

    const paymentsTotal = (tournament.finances?.payments || []).reduce((sum, p) => sum + p.amount, 0);
    const sponsorsTotal = (tournament.finances?.sponsors || []).reduce((sum, s) => sum + s.amount, 0);
    const donationsTotal = (tournament.finances?.donations || []).reduce((sum, d) => sum + d.amount, 0);
    const canteenTotal = (tournament.finances?.canteenRevenue || []).reduce((sum, c) => sum + c.amount, 0);
    const totalRevenue = paymentsTotal + sponsorsTotal + donationsTotal + canteenTotal;

    return { participants, totalRevenue };
  }, [tournament, entries]);

  if (loading) return <p>Lade Finanzdaten...</p>;
  if (!tournament) return <p className="error-message">Turnier nicht gefunden.</p>;

  const finances = tournament.finances || {};

  return (
    <div className="screen-container">
      <div className="page-header-with-action">
        <h2>Finanzübersicht für {tournament.name}</h2>
        <div>
          <button onClick={() => generateSingleReceiptPDF(null, null)} className="btn-secondary-action">
            Blanko-Quittung
          </button>
          <button onClick={generateAllReceiptsPDF} className="btn-secondary-action">
            Alle Quittungen (Blanko)
          </button>
          <button onClick={handleSubmit} className="btn-primary-action">Finanzen speichern</button>
        </div>
      </div>
      
      <div className="layout-grid">
        <div className="grid-column">
          <div className="finance-section">
            <h3>Startgelder</h3>
            <table>
              <thead><tr><th>Teilnehmer</th><th>Soll</th><th>Bezahlt (€)</th><th>Aktion</th></tr></thead>
              <tbody>
                {participants.map(p => {
                  const fee = p.type === 'team' ? (finances.entryFeeTeam || 36) : (finances.entryFeeSingle || 10);
                  const payment = (finances.payments || []).find(pay => pay.referenceId === p.id);
                  const amountPaid = payment?.amount || 0;
                  return (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{fee.toFixed(2)} €</td>
                      <td><input type="number" step="0.01" value={payment?.amount || ''} onChange={e => handlePaymentChange(p.id, p.name, e.target.value)} placeholder="0.00" /></td>
                      <td>
                        {amountPaid > 0 && (
                          <button 
                            className="btn-print-small" 
                            title="Quittung drucken"
                            onClick={() => generateSingleReceiptPDF(p, amountPaid)}
                          >
                            🖨️
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="finance-section">
            <h3>Sponsoren</h3>
            {(finances.sponsors || []).map((sponsor, index) => (
              <div key={index} className="finance-item">
                <input type="text" value={sponsor.name} onChange={e => handleListChange('sponsors', index, 'name', e.target.value)} placeholder="Name des Sponsors" />
                <input type="number" step="0.01" value={sponsor.amount} onChange={e => handleListChange('sponsors', index, 'amount', parseFloat(e.target.value) || 0)} placeholder="Betrag" />
                <button type="button" onClick={() => handleRemoveListItem('sponsors', index)} className="btn-delete-small">X</button>
              </div>
            ))}
            <button type="button" onClick={() => handleAddListItem('sponsors', { name: '', amount: 0 })}>+ Sponsor hinzufügen</button>
          </div>
        </div>

        <div className="grid-column">
          <div className="finance-section">
            <h3>Kantinenumsatz</h3>
            {(finances.canteenRevenue || []).map((canteen, index) => (
              <div key={index} className="finance-item">
                <input type="date" value={(canteen.date || '').substring(0, 10)} onChange={e => handleListChange('canteenRevenue', index, 'date', e.target.value)} />
                <input type="number" step="0.01" value={canteen.amount} onChange={e => handleListChange('canteenRevenue', index, 'amount', parseFloat(e.target.value) || 0)} placeholder="Umsatz" />
                <button type="button" onClick={() => handleRemoveListItem('canteenRevenue', index)} className="btn-delete-small">X</button>
              </div>
            ))}
            <button type="button" onClick={() => handleAddListItem('canteenRevenue', { date: new Date().toISOString().substring(0,10), amount: 0 })}>+ Tagesumsatz hinzufügen</button>
          </div>

          <div className="finance-section">
            <h3>Spenden</h3>
            {(finances.donations || []).map((donation, index) => (
              <div key={index} className="finance-item">
                <input type="text" value={donation.donor} onChange={e => handleListChange('donations', index, 'donor', e.target.value)} placeholder="Name des Spenders" />
                <input type="number" step="0.01" value={donation.amount} onChange={e => handleListChange('donations', index, 'amount', parseFloat(e.target.value) || 0)} placeholder="Betrag" />
                <button type="button" onClick={() => handleRemoveListItem('donations', index)} className="btn-delete-small">X</button>
              </div>
            ))}
            <button type="button" onClick={() => handleAddListItem('donations', { donor: 'Anonym', amount: 0 })}>+ Spende hinzufügen</button>
          </div>

          <div className="finance-summary">
            <h3>Gesamteinnahmen</h3>
            <div className="total-revenue">
              {totalRevenue.toFixed(2)} €
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceScreen;